import React from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  Button,
  TextField,
  Checkbox,
  CardHeader,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Icon,
  Card,
  Fab,
  FormHelperText
} from '@material-ui/core';
import {editCustomOptionApi} from 'api';
import { uploadFileTypeUnsupported } from 'redux/actions/general';
import {fileToBase64} from 'utils';
import {cacheSwatchImage} from './swatchImageCache';


const AddCustomOptionValue = ({open, onClose, option}) => {
  const dispatch = useDispatch();
  const customOptionValuesSortOrders = option.values.map((o) => o.sort_order);
  return (
    <Dialog
      fullWidth
      maxWidth={'md'}
      open={open}
      onClose={() => onClose(false)}
      aria-labelledby="add-custom-option-value"
    >
      <DialogContent>
        <Formik
          initialValues={{
            price: '',
            price_type: 'fixed',
            sort_order: '',
            title: '',
            sku: option.sku,
            images: [],
            manage_stock: false,
            qty: 0
          }}
          enableReinitialize
          validationSchema={yup.object(
            {
              title: yup
                .string()
                .required('Value name is required')
                .min(2, 'Must be at least 2 characters'),
              sort_order: yup
                .number()
                .notOneOf(customOptionValuesSortOrders, 'Sort order already specified')
                .nullable().typeError('Allowed numbers only')
                .integer('Order must be an integer')
                .min(0, 'Invalid sort_order number')
                .required('Order is required'),
              price: yup
                .number()
                .nullable().typeError('Allowed numbers only')
                .min(0, 'Custom option price must be greater than or equal to 0'),
              images: yup
                .array()
                .test('images', 'Image is required', (value) => {
                  if (option.is_swatch) {
                    return !!value.length;
                  }

                  return true;
                }),
              manage_stock: yup
                .boolean(),
              qty: yup
                .number()
                .when(
                  'manage_stock',
                  {
                    is: true,
                    then: yup
                      .number()
                      .typeError('Allowed numbers only')
                      .min(0, 'Quantity must be greater than or equal to 0')
                      .required('Field is required')
                  }
                )
            })}
          onSubmit={(values) => {
            const newOption = {
              ...option,
              values: [...option.values, values]
            };

            if (values.images?.[0]?.base64) {
              cacheSwatchImage(option.product_sku, values.title, values.sort_order, values.images[0].base64);
            }

            editCustomOptionApi({dispatch, option: newOption});
            onClose(false);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue
          }) => (
            <>
              <form onSubmit={handleSubmit}>
                <CardHeader
                  className="px-0"
                  subheader="Custom Option"
                />
                <CardContent className="px-0 flex justify-between">
                  <TextField
                    className="mx-3 w-full"
                    name="title"
                    label="Title"
                    value={values.title}
                    onChange={(e) => {
                      handleChange(e);

                      if (values.images.length) {
                        setFieldValue('images[0].title_text', e.target.value);
                      }
                    }}
                    onBlur={(e) => {
                      handleBlur(e);

                      if (values.images.length) {
                        setFieldValue('images[0].title_text', e.target.value);
                      }
                    }}
                    inputProps={{ className: 'text-center' }}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                  />
                  <TextField
                    className="mx-3 w-full"
                    name="sort_order"
                    label="Order"
                    value={values.sort_order}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ className: 'text-center' }}
                    error={touched.sort_order && Boolean(errors.sort_order)}
                    helperText={touched.sort_order && errors.sort_order}
                  />
                  <TextField
                    className="mx-3 w-full"
                    name="price"
                    label="Price"
                    value={values.price}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ className: 'text-center' }}
                    error={touched.price && Boolean(errors.price)}
                    helperText={touched.price && errors.price}
                  />
                  <FormControlLabel
                    label="Manage Stock"
                    className="mx-3 w-full"
                    control={
                      <Checkbox
                        color="primary"
                        checked={values.manage_stock}
                        onChange={({target: {checked}}) => setFieldValue('manage_stock', checked)}

                      />
                    }
                  />
                  {values.manage_stock &&
                    <TextField
                      className="mx-3 w-full"
                      name="qty"
                      label="Quantity"
                      value={values.qty}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      inputProps={{
                        className: 'text-center',
                        shrink: 'true'
                      }}
                      error={touched.qty && Boolean(errors.qty)}
                      helperText={touched.qty && errors.qty}
                    />
                  }
                </CardContent>
                {option.is_swatch &&
                <>
                  <CardHeader
                    className="px-0 mt-5"
                    subheader="Image for swatch custom option:"
                  />
                  <CardContent
                    className={`border-1-light-grey border-radius-4 p-8 border-dashed flex flex-wrap
                    ${(Boolean(errors.images) && 'border-error')}
                    `}
                  >
                    {!values.images.length ?
                      <Card elevation={10} className="flex-column h-full items-center justify-center p-8">
                        <div>
                          <Icon fontSize="large" color="primary">cloud_upload</Icon>
                        </div>
                        <span className="font-bold text-14 text-brand">Upload New Image</span>
                        <span className="text-12 mb-4 text-muted">Files supported: JPG and PNG</span>
                        <label htmlFor="upload-swatch-image">
                          <Fab
                            component="span"
                            variant="extended"
                            className="border-radius-30 capitalize bg-white text-14 h-30px text-primary font-bold border-1-primary box-shadow-none"
                          >
                            Browse file
                          </Fab>
                        </label>
                        <input
                          className="hidden"
                          onChange={async (event) => {
                            const {files, value} = event.target;

                            const fileTypeDotIndexPosition = value.lastIndexOf('.') + 1;
                            const slicedFileTypeFromFilePath = value.slice(fileTypeDotIndexPosition);
                            // image validation regex
                            const acceptableFormats = 'png|jpeg';
                            const hasImageFile = new RegExp(`(image\\/(${acceptableFormats}))`, 'gi');

                            if (!hasImageFile.test(event.target.files[0]?.type) && !hasImageFile.test(slicedFileTypeFromFilePath)) {
                              dispatch(uploadFileTypeUnsupported());
                            } else {
                              const file = await fileToBase64(files[0]);
                              const base64Image = btoa(file);

                              const optionValueImages = [
                                {
                                  base64: base64Image,
                                  sort_order: 1,
                                  title_text: values.title
                                }
                              ];
                              setFieldValue('images', optionValueImages);
                            }
                          }}
                          id="upload-swatch-image"
                          type="file"
                          accept="image/png, image/jpeg"
                        />
                      </Card>
                      :
                      <>
                        {values.images.map((imageItem, idx) => (
                          <div
                            key={`swatch-image-${idx}`}
                            className="p-2 max-w-180 border-radius-4 overflow-visible elevation-z5 relative"
                          >
                            <Icon
                              fontSize="small"
                              className="absolute text-muted cursor-pointer"
                              style={{right: '-5px', top: '-5px'}}
                              title="Delete Image"
                              onClick={() => {
                                if (imageItem.option_type_image_id) {
                                  const filteredImages = values.images.filter((image) => (
                                    image.option_type_image_id !== imageItem.option_type_image_id
                                  ));
                                  setFieldValue('images', filteredImages);
                                } else {
                                  setFieldValue('images', []);
                                }
                              }}
                            >
                              cancel
                            </Icon>
                            <img
                              src={imageItem.option_type_image_id
                                ? imageItem.image_url
                                : `data:image/png;base64,${imageItem.base64}`
                              }
                              alt={imageItem.title_text}
                              className="max-w-full"
                            />
                          </div>
                        ))}
                      </>
                    }
                  </CardContent>
                  {Boolean(errors?.images) &&
                    <FormHelperText error>
                      {errors.images}
                    </FormHelperText>
                  }
                </>
                }
                <DialogActions className="m-4">
                  <Button
                    className="text-muted"
                    onClick={() => onClose(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                  >
                    Create Option Value
                  </Button>
                </DialogActions>
              </form>
            </>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AddCustomOptionValue;
